import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PrintIcon from '@mui/icons-material/Print';

import { usePosProfile } from './PosProfileContext';
import useSession from '../auth/useSession';
import { getClosingInvoices, getOpeningEntry } from '../api/posProfile';

/**
 * Shift Summary / Z Report dialog.
 *
 * A printable, read-only report of the current shift including:
 * - Shift info (profile, cashier, duration)
 * - Sales summary (count, gross, returns, net)
 * - Payment breakdown by method
 * - Cash movements
 * - Top items sold
 */
export default function ShiftReportDialog({ open, onClose }) {
  const { profile, openingEntry: openingEntryName, cashMovements, cashMovementNet } = usePosProfile();
  const { user } = useSession();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openingEntry, setOpeningEntry] = useState(null);
  const [invoices, setInvoices] = useState([]);

  // Fetch data when dialog opens
  useEffect(() => {
    if (!open || !openingEntryName) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const entry = await getOpeningEntry(openingEntryName);
        if (cancelled) return;
        setOpeningEntry(entry);

        const invs = await getClosingInvoices({
          start: entry.period_start_date,
          end: new Date().toISOString().replace('T', ' ').split('.')[0],
          posProfile: entry.pos_profile,
          user: entry.user || user,
        });
        if (cancelled) return;
        setInvoices(invs);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load report data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [open, openingEntryName, user]);

  // --- Computed summaries ---

  const salesInvoices = useMemo(() => invoices.filter((i) => !i.is_return), [invoices]);
  const returnInvoices = useMemo(() => invoices.filter((i) => i.is_return), [invoices]);

  const grossSales = useMemo(
    () => salesInvoices.reduce((s, i) => s + (i.grand_total || 0), 0),
    [salesInvoices],
  );
  const totalReturns = useMemo(
    () => returnInvoices.reduce((s, i) => s + Math.abs(i.grand_total || 0), 0),
    [returnInvoices],
  );
  const netSales = grossSales - totalReturns;

  const totalQty = useMemo(
    () => invoices.reduce((s, i) => s + (i.total_qty || 0), 0),
    [invoices],
  );

  // Payment breakdown
  const paymentBreakdown = useMemo(() => {
    const breakdown = {};
    invoices.forEach((inv) => {
      (inv.payments || []).forEach((p) => {
        if (!breakdown[p.mode_of_payment]) breakdown[p.mode_of_payment] = 0;
        breakdown[p.mode_of_payment] += p.amount || 0;
      });
    });
    return breakdown;
  }, [invoices]);

  // Top items sold
  const topItems = useMemo(() => {
    const itemMap = {};
    invoices.forEach((inv) => {
      (inv.items || []).forEach((item) => {
        const key = item.item_code;
        if (!itemMap[key]) {
          itemMap[key] = { item_code: key, item_name: item.item_name, qty: 0, amount: 0 };
        }
        itemMap[key].qty += item.qty || 0;
        itemMap[key].amount += item.amount || 0;
      });
    });
    return Object.values(itemMap)
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 10);
  }, [invoices]);

  const shiftStart = openingEntry?.period_start_date
    ? new Date(openingEntry.period_start_date).toLocaleString()
    : '—';
  const shiftNow = new Date().toLocaleString();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AssessmentIcon />
          <span>Shift Report (Z Report)</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography color="text.secondary">Loading report...</Typography>
          </Stack>
        ) : (
          <Box id="shift-report-printable">
            {/* Header */}
            <Typography variant="h6" textAlign="center" fontWeight={700} sx={{ mb: 0.5 }}>
              Shift Summary
            </Typography>
            <Typography variant="body2" textAlign="center" color="text.secondary" sx={{ mb: 2 }}>
              {profile?.name} — {user}
            </Typography>

            {/* Shift info */}
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Row label="Shift Start" value={shiftStart} />
              <Row label="Report Time" value={shiftNow} />
              <Row label="Opening Entry" value={openingEntryName} />
            </Stack>

            <Divider />

            {/* Sales summary */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              Sales Summary
            </Typography>
            <Stack spacing={0.5}>
              <Row label="Total Invoices" value={salesInvoices.length} bold />
              <Row label="Returns" value={returnInvoices.length} />
              <Row label="Total Items Sold" value={totalQty} />
              <Divider sx={{ my: 0.5 }} />
              <Row label="Gross Sales" value={grossSales.toFixed(2)} bold />
              {totalReturns > 0 && (
                <Row label="Total Returns" value={`-${totalReturns.toFixed(2)}`} color="error.main" />
              )}
              <Row label="Net Sales" value={netSales.toFixed(2)} bold size="subtitle1" />
            </Stack>

            <Divider sx={{ my: 2 }} />

            {/* Payment breakdown */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Payments by Method
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Method</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(paymentBreakdown).map(([method, amount]) => (
                    <TableRow key={method}>
                      <TableCell>{method}</TableCell>
                      <TableCell align="right">{amount.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Cash movements */}
            {cashMovements.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Cash Movements
                </Typography>
                <Stack spacing={0.5}>
                  {cashMovements.map((m) => (
                    <Stack key={m.id} direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={m.type === 'in' ? 'IN' : 'OUT'}
                          size="small"
                          color={m.type === 'in' ? 'success' : 'error'}
                        />
                        <Typography variant="body2">{m.note || '—'}</Typography>
                      </Stack>
                      <Typography variant="body2" fontWeight={600}>
                        {m.type === 'in' ? '+' : '-'}{m.amount.toFixed(2)}
                      </Typography>
                    </Stack>
                  ))}
                  <Divider sx={{ my: 0.5 }} />
                  <Row
                    label="Net Cash Adjustment"
                    value={`${cashMovementNet >= 0 ? '+' : ''}${cashMovementNet.toFixed(2)}`}
                    bold
                    color={cashMovementNet >= 0 ? 'success.main' : 'error.main'}
                  />
                </Stack>
              </>
            )}

            {/* Top items */}
            {topItems.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Top Items
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topItems.map((item) => (
                        <TableRow key={item.item_code}>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {item.item_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{item.qty}</TableCell>
                          <TableCell align="right">{item.amount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Button startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Reusable summary row */
function Row({ label, value, bold, color, size = 'body2' }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant={size} color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant={size}
        fontWeight={bold ? 700 : 400}
        color={color || 'text.primary'}
      >
        {value}
      </Typography>
    </Stack>
  );
}
